import { Injectable, Injector } from '@angular/core';
import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { TabService } from './tab.service';
import { ROUTE_TAB_CONFIG } from './route-tab.config';

/**
 * Custom RouteReuseStrategy that preserves component state for MDI tabs.
 * When navigating away from a tab, the component is stored and reattached
 * when returning to that tab, preserving all state including form data,
 * scroll position, and component state.
 */
@Injectable()
export class TabRouteReuseStrategy implements RouteReuseStrategy {
  /** Map of stored route handles keyed by route path */
  private storedHandles = new Map<string, DetachedRouteHandle>();

  private _tabService: TabService | null = null;
  private get tabService(): TabService {
    if (!this._tabService) {
      this._tabService = this.injector.get(TabService);
    }
    return this._tabService;
  }

  constructor(private injector: Injector) {
    // Delay subscription to ensure TabService and its dependencies are ready
    setTimeout(() => {
      this.tabService.tabsChanged.subscribe((tabs) => {
        this.cleanupOrphanedHandles(tabs.map((t) => t.id));
      });
    });
  }

  /**
   * Determines if the route should be stored.
   * Returns true for routes that are part of the tab system.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // Angular router crashes with Maximum Call Stack if a lazy-loaded wrapper is cached and retrieved.
    if (route.routeConfig && (route.routeConfig as any).loadChildren) {
      return false;
    }

    const path = this.getFullPath(route);
    const shouldDetach = this.isTabRoute(path);
    console.log('[RouteReuse] shouldDetach:', { path, shouldDetach });
    return shouldDetach;
  }

  /**
   * Store the detached route handle.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (handle) {
      const path = this.getFullPath(route);
      console.log('[RouteReuse] storing handle for path:', path);
      this.storedHandles.set(path, handle);
    }
  }

  /**
   * Determines if a stored route should be reattached.
   * Returns true if we have a valid (non-destroyed) stored handle for this route.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const path = this.getFullPath(route);
    const handle = this.storedHandles.get(path);

    if (handle) {
      // Check if the cached component view has been destroyed
      const componentRef = (handle as any).componentRef;
      if (componentRef && componentRef.hostView && componentRef.hostView.destroyed) {
        console.log('[RouteReuse] Stored component was destroyed, removing from cache:', path);
        this.storedHandles.delete(path);
        return false;
      }
      console.log('[RouteReuse] shouldAttach: true for path:', path);
      return true;
    }

    console.log('[RouteReuse] shouldAttach: false for path:', path);
    return false;
  }

  /**
   * Retrieve the stored route handle.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    // Never retrieve for lazy-loaded wrappers to prevent infinite router loops
    if (route.routeConfig && (route.routeConfig as any).loadChildren) {
      return null;
    }

    const path = this.getFullPath(route);
    const handle = this.storedHandles.get(path);

    if (handle) {
      // Double check it's not destroyed before returning
      const componentRef = (handle as any).componentRef;
      if (componentRef && componentRef.hostView && componentRef.hostView.destroyed) {
        this.storedHandles.delete(path);
        return null;
      }
      return handle;
    }

    return null;
  }

  /**
   * Determines if the route should be reused.
   * By default, Angular reuses routes if the route config is the same.
   * We want to always return false to allow our custom attach/detach logic.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // Check if routes are the same including params
    const futurePath = this.getFullPath(future);
    const currPath = this.getFullPath(curr);

    // If paths are the same, reuse the route
    if (futurePath === currPath) {
      return true;
    }

    // If both are tab routes, don't reuse - let our store/retrieve handle it
    if (this.isTabRoute(futurePath) && this.isTabRoute(currPath)) {
      return false;
    }

    // Default Angular behavior
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Get the full path from an ActivatedRouteSnapshot including params.
   */
  private getFullPath(route: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.url.length > 0) {
        segments.unshift(...current.url.map((segment) => segment.path));
      }
      current = current.parent;
    }

    // Ensure we have a leading slash and handle empty path
    const path = '/' + segments.join('/');
    return path === '/' ? '' : path;
  }

  /**
   * Check if a path corresponds to a tab route that should be cached.
   * This includes both exact matches and parameterized routes.
   */
  private isTabRoute(path: string): boolean {
    if (!path) return false;

    // Check if the path matches any base path in ROUTE_TAB_CONFIG
    // Parameterized routes (like /dashboard/patients/patient-record/email@example.com)
    // match if they start with a registered base path
    return Object.keys(ROUTE_TAB_CONFIG).some((basePath) => {
      return path === basePath || path.startsWith(basePath + '/');
    });
  }

  /**
   * Remove stored handles for closed tabs.
   */
  private cleanupOrphanedHandles(activeTabIds: string[]): void {
    const keysToRemove: string[] = [];

    this.storedHandles.forEach((_, path) => {
      // Only cleanup paths that ARE tab routes (registered in ROUTE_TAB_CONFIG)
      // but are NOT in the active tabs list.
      if (this.isTabRoute(path) && !activeTabIds.includes(path)) {
        keysToRemove.push(path);
      }
    });

    keysToRemove.forEach((key) => {
      const handle = this.storedHandles.get(key);
      if (handle) {
        this.destroyComponent(handle);
      }
      this.storedHandles.delete(key);
    });
  }

  /**
   * Properly destroy a stored component to prevent memory leaks.
   */
  private destroyComponent(handle: DetachedRouteHandle): void {
    try {
      // The handle contains the component ref which can be destroyed
      const componentRef = (handle as any)?.componentRef;
      if (componentRef && typeof componentRef.destroy === 'function') {
        componentRef.destroy();
      }
    } catch (error) {
      console.warn('Error destroying cached component:', error);
    }
  }

  /**
   * Clear a specific stored handle (e.g., when tab is closed).
   */
  clearHandle(path: string): void {
    const handle = this.storedHandles.get(path);
    if (handle) {
      this.destroyComponent(handle);
      this.storedHandles.delete(path);
    }
  }

  /**
   * Clear all stored handles.
   */
  clearAllHandles(): void {
    this.storedHandles.forEach((handle) => {
      this.destroyComponent(handle);
    });
    this.storedHandles.clear();
  }
}
